import {
  Card,
  CardBody,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, useState, React, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { MainContext } from "context/MainContext.js";

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";

const ProfileList = () => {
  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);
  const [alert, setAlert] = useState(null);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["installations.site.list"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const [siteId, setSiteId] = useState(location.state?.siteId ?? "");
  const [siteName, setSiteName] = useState(location.state?.siteName ?? "");

  const redirectToEdit = (profileId, profileName) => {
    const location = {
      pathname: "/admin/installations/profile/edit",
      state: {
        id: profileId ? profileId : "",
        name: profileName ? profileName : "",
        mode: profileName ? "edit" : "create",
      },
    };
    history.push(location);
  };

  const deleteConfirm = (id, name) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={"Are you sure you want to delete this profile?"}
        onConfirm={() => {
          deleteProfile(id);
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="info"
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
        btnSize=""
      >
        {"You will not be able to recover this profile "}'{name}'
      </ReactBSAlert>,
    );
  };

  const errorDelete = (errorMessage) => {
    setAlert(
      <ReactBSAlert
        error
        style={{ display: "block", marginTop: "-100px" }}
        title="Error!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {errorMessage}
      </ReactBSAlert>,
    );
  };

  const successDelete = () => {
    setAlert(
      <ReactBSAlert
        success
        style={{ display: "block", marginTop: "-100px" }}
        title="Deleted!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {"The profile has been deleted"}
      </ReactBSAlert>,
    );
  };

  const deleteProfile = (id) => {
    const start = Date.now();

    // Call the API
    fetch("/api/v1/profile/delete", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        profileId: id,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (actualData.status != "200") {
          errorDelete(
            actualData.error_code
              ? actualData.error_code[0]
              : actualData.status,
          );
        } else {
          successDelete();
          setForceRefreshCount((prevValue) => ++prevValue);
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Cell: (props) => {
          if (props.row.original.siteLock) {
            return props.value;
          } else {
            return (
              <a
                onClick={() =>
                  redirectToEdit(props.row.original.id, props.row.original.name)
                }
                style={{
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {props.value}
              </a>
            );
          }
        },
        sortBy: "name",
        flex: "1 1 0",
      },
      {
        Header: "Vlan Port #1",
        accessor: "vlan",
        sortBy: "p.vlan",
        flex: "1 1 0",
      },
      {
        Header: "Vlan Port #2",
        accessor: "vlan2",
        sortBy: "p.vlan2",
        flex: "1 1 0",
      },
      {
        Header: "POE",
        accessor: "poeModeCtrl",
        sortBy: "p.poe_mode_ctrl",
        flex: "1 1 0",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
      },
      {
        Header: "Bandwidth",
        accessor: "bandwidth",
        sortBy: "b.name",
        flex: "1 1 0",
        show: false,
      },
      {
        Header: "Trunk Mode",
        accessor: "trunkMode",
        sortBy: "p.trunk_mode",
        flex: "1 1 0",
        show: false,
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
      },
      {
        Header: "Double Tags",
        accessor: "doubleTags",
        sortBy: "p.double_tags",
        flex: "1 1 0",
        show: false,
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
      },
      {
        Header: "Endpoint Tagging",
        accessor: "endpointTagging",
        sortBy: "p.endpoint_tagging",
        flex: "1 1 0",
        show: false,
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
      },
      {
        Header: "Endpoint Tagging Port #2",
        accessor: "endpointTagging2",
        sortBy: "p.endpoint_tagging2",
        flex: "1 1 0",
        show: false,
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
      },
      {
        Header: "Outer Tag",
        accessor: "outerTag",
        sortBy: "p.outer_tag",
        flex: "1 1 0",
        show: false,
      },
      {
        Header: "Remapped VID",
        accessor: "remappedVID",
        sortBy: "p.remappedvid",
        flex: "1 1 0",
        show: false,
      },
      {
        Header: "Allowed Tagged Vlan Port #1",
        accessor: "allowedTaggedVlan",
        sortBy: "p.allowed_tagged_vlan",
        flex: "1 1 0",
        show: false,
      },
      {
        Header: "Allowed Tagged Vlan Port #2",
        accessor: "allowedTaggedVlan2",
        sortBy: "p.allowed_tagged_vlan2",
        flex: "1 1 0",
        show: false,
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          if (props.row.original.siteLock) {
            return "";
          } else {
            return (
              <UncontrolledDropdown direction="down">
                <DropdownToggle
                  caret
                  color="info"
                  style={{
                    padding: "6px 14px 6px 14px",
                  }}
                >
                  Actions
                </DropdownToggle>
                <DropdownMenu container={"body"}>
                  <DropdownItem
                    onClick={() =>
                      redirectToEdit(
                        props.row.original.id,
                        props.row.original.name,
                      )
                    }
                  >
                    Edit Profile
                  </DropdownItem>
                  <DropdownItem
                    onClick={() =>
                      deleteConfirm(
                        props.row.original.id,
                        props.row.original.name,
                      )
                    }
                  >
                    Delete Profile
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            );
          }
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <div className="content">
      {alert}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              {siteId ? (
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Automations</BreadcrumbItem>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/installations/site/list",
                      }}
                    >
                      {t(
                        "sites.breadcrumb.sites.t." +
                          sessionStorage.getItem("bundleType"),
                        "Sites",
                      )}
                    </NavLink>
                    {siteName ? <i>&lt;{siteName}&gt;</i> : ""}
                  </BreadcrumbItem>
                  <BreadcrumbItem>Profiles</BreadcrumbItem>
                </Breadcrumb>
              ) : (
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Profiles</BreadcrumbItem>
                </Breadcrumb>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <ReactTable
                query="/api/v1/profile/list"
                columns={columns}
                param={siteId}
                title="profiles"
                className="-striped -highlight primary-pagination"
                forceRefresh={forceRefreshCount}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProfileList;
